import React, { useContext, useEffect, useMemo, useState } from "react";
import { usePost } from "../../API";
import { Button, Icon, Input, MultiSelect, Select, SelectOptionType, useToast } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { ApplicationState, ChatMessageItemTypes } from "@aidkitorg/types/lib/convoai";
import { ApplicantProvider } from "../../Contexts/ApplicantContext";
import { ApplicationStateContext, ApplicationStateProvider } from "../../Contexts/ApplicationStateContext";
import { ModularQuestionPage } from "../../ModularQuestionPage";
import { Sections } from "../../Apply";
import { InfoDict } from "../../Questions/Props";
import { v4 } from 'uuid';

type AppStateType = ChatMessageItemTypes['appState'];

const normalizeSurveyPath = (value?: string): string | undefined => {
  if (!value) {
    return value;
  }
  return value.replace(/^\/*/, "");
};

const toAppStateOption = (row: AppStateType): SelectOptionType<string> => {
  const info = row.appState?.info;
  const email =
    info &&
      typeof info === "object" &&
      "email" in info &&
      typeof info.email === "string" &&
      info.email.length > 0
      ? info.email
      : null;
  const tagLabel = row.meta?.tags?.length
    // shows the first two tags on the app state item
    ? `${row.meta.tags.slice(0, 2).join(", ")}${row.meta.tags.length > 2 ? ` +${row.meta.tags.length - 2}` : ""}`
    : "no tags";

  return {
    value: row.id,
    label: `${row.id.slice(0, 8)}... | ${new Date(row.meta!.time!).toLocaleString()}${email ? ` | ${email}` : ""} | tags: ${tagLabel}`,
  };
};

function AppStateSync({ onChange }: { onChange: (state: ApplicationState) => void }) {
  const { state } = useContext(ApplicationStateContext);

  useEffect(() => {
    if (Object.keys(state.questions).length === 0 && Object.keys(state.sections).length === 0) {
      return;
    }
    onChange(state);
  }, [state, onChange]);

  return null;
}

export function AppStatesConfig() {
  const listAppStates = usePost("/program/admin/app_states/list");
  const createAppState = usePost("/program/admin/app_states/create");
  const getAllTags = usePost("/coach/conversation/tags/all");
  const getSurvey = usePost("/subsurvey/definition", { handleErrors() { } });
  const { toast } = useToast();

  const [rows, setRows] = useState<AppStateType[]>([]);
  const [selectedAppStateOption, setSelectedAppStateOption] = useState<SelectOptionType<string> | undefined>();
  const [currentSurveyPath, setCurrentSurveyPath] = useState<string | undefined>();
  const [sections, setSections] = useState<Sections | null>(null);
  const [info, setInfo] = useState<InfoDict>({});
  const [recomputedState, setRecomputedState] = useState<ApplicationState | null>(null);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState<SelectOptionType[]>([]);
  const [sourceTagFilters, setSourceTagFilters] = useState<SelectOptionType[]>([]);
  const [selectedTags, setSelectedTags] = useState<SelectOptionType[]>([]);
  const [createMode, setCreateMode] = useState<"source" | "scratch">("source");
  const [scratchConversationId, setScratchConversationId] = useState<string | undefined>();
  const selectedItemId = selectedAppStateOption?.value ?? "";

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedItemId) ?? null,
    [rows, selectedItemId],
  );

  const options = useMemo(
    (): SelectOptionType<string>[] => rows.map((row) => toAppStateOption(row)),
    [rows],
  );

  const loadAppStateRows = async (preferConversationItemId?: string) => {
    const resp = await listAppStates({ limit: sourceTagFilters.length ? undefined : 100, tags: sourceTagFilters.map(t => t.value) });
    const items = (resp.items ?? []);
    setRows(items);
    setSelectedAppStateOption((current) => {
      const preferredRow = preferConversationItemId ? items.find((i) => i.id === preferConversationItemId) ?? null : null;
      if (preferredRow) return toAppStateOption(preferredRow);
      if (current?.value) {
        const currentRow = items.find((i) => i.id === current.value) ?? null;
        if (currentRow) return toAppStateOption(currentRow);
      }
      return undefined;
    });
  };

  useAsyncEffect(async () => {
    await loadAppStateRows();
  }, [sourceTagFilters]);

  useAsyncEffect(async () => {
    const { tags = [] } = await getAllTags({});
    setAvailableTags(tags.map((tag) => ({ value: tag, label: tag })));
  }, []);

  const reloadSurvey = async (surveyPath?: string) => {
    if (!surveyPath) return;
    let actualSurveyName = surveyPath;
    if (surveyPath.includes('/p/') || surveyPath.includes('/a/')) {
      actualSurveyName = surveyPath.split("/").pop() ?? surveyPath;
    } else if (surveyPath.startsWith('/apply') || surveyPath.startsWith('apply')) {
      actualSurveyName = 'apply';
    }
    setLoadingSurvey(true);
    try {
      const survey = await getSurvey({ form_name: actualSurveyName });
      if (!survey.error) {
        setSections(survey as Sections);
        toast({
          description: `Loaded survey "${surveyPath}"`,
          autoClose: true,
          duration: 1_000,
        });
      }
    } finally {
      setLoadingSurvey(false);
    }
  };

  useAsyncEffect(async () => {
    if (createMode === "scratch") {
      return;
    }
    if (!selectedRow?.appState) {
      setSections(null);
      setInfo({});
      setRecomputedState(null);
      setSelectedTags([]);
      return;
    }

    const sourcePath = normalizeSurveyPath(selectedRow.appState.currentSurveyPath);
    setCurrentSurveyPath(sourcePath);
    setInfo((selectedRow.appState.info ?? {}) as InfoDict);
    setRecomputedState(selectedRow.appState);

    if (sourcePath !== currentSurveyPath) {
      await reloadSurvey(sourcePath);
    }

  }, [createMode, selectedRow?.id]);

  useEffect(() => {
    if (createMode !== "source") {
      return;
    }
    const sourceTags = selectedRow?.meta?.tags ?? [];
    setSelectedTags(sourceTags.map((tag) => ({ value: tag, label: tag })));
  }, [createMode, selectedRow]);

  useEffect(() => {
    if (selectedAppStateOption && !rows.some((row) => row.id === selectedAppStateOption.value)) {
      // Defer clearing to the next frame so Select/MultiSelect layout work
      // does not fight ResizeObserver measurements in the same tick.
      const rafId = window.requestAnimationFrame(() => {
        setSelectedAppStateOption(undefined);
      });
      return () => window.cancelAnimationFrame(rafId);
    }
  }, [rows, selectedAppStateOption]);

  const saveRecomputed = async () => {
    if (!recomputedState) {
      return;
    }
    if (createMode === "source" && !selectedRow) {
      return;
    }
    setSaving(true);
    try {
      const appState = {
        ...recomputedState,
        currentSurveyPath,
      };
      const created = await createAppState({ appState, tags: selectedTags.map(t => t.value) });

      toast({
        description: `Saved new appState item ${created.id}${createMode === "scratch" ? " from scratch" : ""}${selectedTags.length ? ` with ${selectedTags.length} tag(s)` : ""}`,
        autoClose: true,
        duration: 2_000,
      });
      await loadAppStateRows(created.itemId);
      setCreateMode("source");
    } finally {
      setSaving(false);
    }
  };

  const startFromScratch = () => {
    setCreateMode("scratch");
    setScratchConversationId(v4());
    const initialSurveyPath = normalizeSurveyPath(selectedRow?.appState?.currentSurveyPath ?? currentSurveyPath);
    setCurrentSurveyPath(initialSurveyPath);
    setSelectedAppStateOption(undefined);
    setSections(null);
    setInfo({});
    setRecomputedState({
      sections: {},
      questions: {},
      currentSurveyPath: initialSurveyPath,
      info: {},
    });
    setSelectedTags([]);
  };

  const startFromSource = () => {
    setCreateMode("source");
    if (!selectedAppStateOption) {
      setSections(null);
      setInfo({});
      setRecomputedState(null);
      setSelectedTags([]);
    }
  };

  const canSave = recomputedState && !saving && (createMode === "scratch" || (createMode === "source" && selectedRow));

  return (
    <div className="bg-gray-100 h-full p-4 space-y-4">
      <h1 className="text-xl font-bold">App State Creator</h1>

      <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={createMode === "source" ? "primary" : "secondary"} onClick={startFromSource} isDisabled={saving}>
            Derive from Source Item
          </Button>
          <Button variant={createMode === "scratch" ? "primary" : "secondary"} onClick={startFromScratch} isDisabled={saving}>
            Create from Scratch
          </Button>
        </div>

        {createMode === "source" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <MultiSelect
              label="Source Tags"
              description="Filter source app states by tag"
              showDescription
              placeholder="Select tags to filter source items"
              options={availableTags}
              value={sourceTagFilters}
              onChange={(next) => {
                // Defer filter updates by one frame to avoid same-frame
                // reflow loops while the MultiSelect popover is measuring.
                window.requestAnimationFrame(() => {
                  setSourceTagFilters(next);
                });
              }}
              showTags
              searchable
            />
            <Select
              label="Source App State"
              description={`Showing ${rows.length} source items`}
              showDescription
              searchable
              options={options}
              value={selectedAppStateOption}
              onChange={setSelectedAppStateOption}
              clearable
            />
          </div>
        )}

        {createMode === "scratch" && (
          <div className="grid grid-cols-1 gap-3">
            <Input
              label="Target Conversation ID"
              description="Generated random conversation ID for this scratch run"
              value={scratchConversationId ?? ""}
              readOnly
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5 max-w-full sm:max-w-2xl">
          <label className="text-sm/6 font-medium text-[var(--ak-color-typography-text)]" htmlFor="survey-path-input">
            Survey Path
          </label>
          <p className="text-xs text-gray-500" id="survey-path-description">
            Form name or path for the survey definition (e.g. apply, /apply, /p/program-slug/apply).
          </p>
          <div className="flex items-stretch rounded-lg ring-1 ring-gray-300 overflow-hidden focus-within:ring-1 focus-within:ring-indigo-600 focus-within:ring-offset-0 bg-white">
            <div className="shrink-0 px-3 py-2 border-r border-gray-300 text-gray-600 font-mono text-sm">
              /
            </div>
            <div className="flex-1 min-w-0 flex flex-col [&>div]:max-w-none [&>div]:flex-1 [&>div]:min-h-0">
              <Input
                id="survey-path-input"
                label="Survey Path"
                showLabel={false}
                description={undefined}
                value={currentSurveyPath}
                onChange={(e) => setCurrentSurveyPath(normalizeSurveyPath(e.target.value))}
                className="w-full rounded-none border-0 ring-0 focus:ring-0 focus-visible:ring-0 font-mono text-sm bg-transparent shadow-none h-10"
                aria-describedby="survey-path-description"
              />
            </div>
            <Button
              icon={<Icon name="ArrowPathIcon" />}
              variant="unstyled"
              onClick={() => reloadSurvey('/' + currentSurveyPath)}
              isDisabled={loadingSurvey || !currentSurveyPath}
              loading={loadingSurvey}
              className="shrink-0 rounded-none border-l border-gray-300 px-3 py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:hover:bg-transparent"
              aria-label="Reload survey"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <MultiSelect
            label="New Item Tags"
            description="Tags to apply to the newly created app state item"
            placeholder="Select or create tags"
            options={availableTags}
            value={selectedTags}
            onChange={setSelectedTags}
            showTags
            customAllowed
            searchable
            customOptionBuilder={(query) => ({ value: query.trim(), label: query.trim() })}
            onAddCustomOption={(option) => {
              if (!option.value.trim()) return;
              setAvailableTags((prev) => {
                if (prev.some((existing) => existing.value === option.value)) {
                  return prev;
                }
                return [...prev, option];
              });
            }}
          />
          <div className="flex items-start justify-start lg:justify-end pt-7">
            <Button onClick={saveRecomputed} isDisabled={!canSave} loading={saving}>
              Save as New Item
            </Button>
          </div>
        </div>

        {createMode === "source" && selectedRow && (
          <div className="text-sm text-gray-600 space-y-1">
            source item: <code>{selectedRow.id}</code> | created by: <code>{selectedRow.createdBy}</code>
            <div>
              will create a new app state from <code>{selectedRow.id}</code> using <code>{currentSurveyPath || "no survey path"}</code> with <code>{selectedTags.length}</code> tag(s)
            </div>
          </div>
        )}
        {createMode === "scratch" && (
          <div className="text-sm text-gray-600">
            will create a new app state from scratch using <code>{currentSurveyPath || "no survey path"}</code> with <code>{selectedTags.length}</code> tag(s)
          </div>
        )}
      </div>

      <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-4">
        {sections ? (
          <ApplicationStateProvider key={`${selectedRow?.id ?? "empty"}:${createMode}`}>
            <ApplicantProvider context={{ info, update: setInfo }}>
              <AppStateSync onChange={setRecomputedState} />
              <ModularQuestionPage
                sections={sections}
                sequential
                Viewer="applicant"
                noNavBar
                noHistory
                excludeAppStateContext
              />
            </ApplicantProvider>
          </ApplicationStateProvider>
        ) : (
          <div className="text-sm text-gray-500">Select a source appState item or use Create from Scratch, then load survey content.</div>
        )}
      </div>
    </div>
  );
}
